'use client';

import { fetchRecommendProducts } from '@/api/product';
import { listByProductIds } from '@/api/public';
import InfiniteScroll from '@/components/InfiniteScroll';
import ProductCard from '@/components/Product/ProductCard';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSpringSaleCache } from '../hooks/useSpringSaleCache';

interface YouMightLikeProps {
  deviceType: EPlatform;
  onLoadComplete?: (isComplete: boolean) => void; // 通知父组件加载完成状态
  isInApp?: boolean; // 是否在 APP 内嵌环境
  receivedAppWishData?: number[]; // APP 传递的已收藏商品ID数组
  onProductClick?: (product: any, index: number) => void;
  onProductExp?: (product: any, index: number) => void;
}

export default function YouMightLike({
  deviceType,
  onLoadComplete,
  isInApp = false,
  receivedAppWishData = [],
  onProductClick,
  onProductExp,
}: YouMightLikeProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const t = useTranslations('activity.flashSale');

  // 获取用户 token，用于监听登录状态变化
  const token = useStore((state) => state.token);
  const prevTokenRef = useRef<string | undefined>(undefined);

  // 使用本地Zustand store管理缓存
  const cachedRecommendList = useSpringSaleCache((state) => state.recommendList);
  const cachedWaterfallPosition = useSpringSaleCache((state) => state.waterfallPosition);
  const cachedAllLoaded = useSpringSaleCache((state) => state.allRecommendListLoaded);
  const setCache = useSpringSaleCache((state) => state.setCache);

  // 从缓存中恢复数据
  const [waterfallList, setWaterfallList] = useState<any[]>(cachedRecommendList);
  const [loading, setLoading] = useState(false);
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(cachedAllLoaded);
  const [waterfallPosition, setWaterfallPosition] = useState(cachedWaterfallPosition);
  const [hasInitialized, setHasInitialized] = useState(false);

  // 标题曝光检测
  const { ref: titleRef } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 更新商品收藏状态
  const updateProductsWishStatus = useCallback(async () => {
    try {
      if (!waterfallList.length) return;

      // 获取所有商品ID
      const productIds = waterfallList.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });

      if (response?.code === 200 && Array.isArray(response.data?.list)) {
        // 创建一个 id -> is_wish 的映射
        const wishMap = response.data.list.reduce<Record<number, number>>((acc, item) => {
          if (item.id && typeof item.is_wish === 'number') {
            acc[item.id] = item.is_wish;
          }
          return acc;
        }, {});

        // 更新列表数据，只更新 is_wish 字段
        setWaterfallList((prevList) => {
          const updatedList = prevList.map((item) => ({
            ...item,
            is_wish: item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : item.is_wish,
          }));

          // 同步更新缓存
          setCache({
            recommendList: updatedList,
            waterfallPosition,
            allRecommendListLoaded,
          });

          return updatedList;
        });
      }
    } catch (error) {
      logger.error('更新商品收藏状态失败:', error);
    }
  }, [waterfallList, waterfallPosition, allRecommendListLoaded]);

  // 初始化加载推荐数据
  useEffect(() => {
    if (hasInitialized) return;

    // 如果有缓存数据且列表不为空，不重新加载
    if (cachedRecommendList && cachedRecommendList.length > 0) {
      setHasInitialized(true);
      return;
    }

    if (isInApp) {
      // APP 内嵌环境：延迟 2 秒后加载数据，给 APP 通信足够时间获取用户信息
      setTimeout(() => {
        loadRecommendData();
      }, 2000);

      setHasInitialized(true);
    } else {
      // 非 APP 环境：立即加载数据
      loadRecommendData();
      setHasInitialized(true);
    }
  }, []);

  // 监听 token 变化，处理登录/退出登录逻辑
  useWhenTokenReady(() => {
    // 首次初始化，记录当前token，但不触发更新
    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      return;
    }

    const prevToken = prevTokenRef.current;

    // 退出登录：token 从有值变为空
    if (prevToken && !token) {
      // 清除所有商品的收藏状态（is_wish 设为 2 表示未收藏）
      setWaterfallList((prevList) => {
        const updatedList = prevList.map((item) => ({
          ...item,
          is_wish: 2,
        }));

        // 同步更新缓存
        setCache({
          recommendList: updatedList,
          waterfallPosition,
          allRecommendListLoaded,
        });

        return updatedList;
      });
    }

    // 登录：token 从空变为有值
    if (!prevToken && token) {
      // 调用接口更新商品收藏状态，而不是重新加载数据
      updateProductsWishStatus();
    }

    // 更新上一次的 token 值
    prevTokenRef.current = token;
  }, [token, waterfallPosition, allRecommendListLoaded, updateProductsWishStatus, setCache]);

  // 监听 APP 传递的商品收藏数据并更新对应商品状态
  useEffect(() => {
    if (!receivedAppWishData || receivedAppWishData.length === 0) {
      return;
    }

    logger.log('收到 APP 传递的收藏数据，开始更新商品状态:', receivedAppWishData);

    // 更新商品列表中对应商品的收藏状态
    setWaterfallList((prevList) => {
      const updatedList = prevList.map((item) => {
        if (item.id) {
          // 检查商品ID是否在已收藏的数组中
          const isInWishList = receivedAppWishData.includes(item.id);
          const newIsWish = isInWishList ? 1 : 2;

          // 只有状态变化时才记录日志
          if (item.is_wish !== newIsWish) {
            logger.log(`更新商品 ${item.id} 收藏状态: ${item.is_wish} -> ${newIsWish}`);
          }

          return {
            ...item,
            is_wish: newIsWish,
          };
        }
        return item;
      });

      // 同步更新缓存
      setCache({
        recommendList: updatedList,
        waterfallPosition,
        allRecommendListLoaded,
      });

      return updatedList;
    });
  }, [receivedAppWishData]);

  // 保存数据到本地store缓存
  useEffect(() => {
    setCache({
      recommendList: waterfallList,
      waterfallPosition,
      allRecommendListLoaded,
    });
  }, [waterfallList, waterfallPosition, allRecommendListLoaded, setCache]);

  // 通知父组件加载完成状态
  useEffect(() => {
    onLoadComplete?.(allRecommendListLoaded);
  }, [allRecommendListLoaded, onLoadComplete]);

  // 加载推荐数据
  const loadRecommendData = async () => {
    if (allRecommendListLoaded || loading) return;

    setLoading(true);
    try {
      const count = await getRecommendList();
      if (count === 0) {
        setAllRecommendListLoaded(true);
      }
    } catch (error) {
      logger.error('Failed to load recommend data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取推荐商品列表
  const getRecommendList = async (): Promise<number> => {
    try {
      // 6: 活动专题页推荐
      const response = await fetchRecommendProducts(
        env.NEXT_PUBLIC_SITE_NAME === 'us'
          ? {
              group_id: 6,
              strategy: 3,
              waterfall_position: waterfallPosition,
            }
          : {
              group_id: 6,
            },
      );

      if (response.code === 200 && response.data) {
        const waterfall_list: any[] =
          env.NEXT_PUBLIC_SITE_NAME === 'us'
            ? response.data.waterfall_list || []
            : response.data.frame_list || [];
        const { waterfall_position: newPosition } = response.data;

        // 确保是有效的商品数组
        const validProducts = waterfall_list.filter((item: any) => {
          return item && typeof item === 'object' && (item.name || item.short_name);
        });

        if (validProducts.length > 0) {
          // 追加新数据到列表
          setWaterfallList((prev) => [...prev, ...validProducts]);
          setWaterfallPosition(newPosition);
          if (env.NEXT_PUBLIC_SITE_NAME !== 'us') {
            setAllRecommendListLoaded(true);
          }
          return validProducts.length;
        } else {
          setAllRecommendListLoaded(true);
          return 0;
        }
      }
    } catch (error) {
      logger.error('Failed to fetch recommend products:', error);
      setAllRecommendListLoaded(true);
    }
    return 0;
  };

  // 处理商品点击
  const handleItemClick = (item: any, index: number) => {
    onProductClick?.(item, index);
  };

  // 处理商品曝光
  const handleProductInView = (item: any, index: number) => {
    onProductExp?.(item, index);
  };

  // 如果没有数据且加载完成，不显示组件
  if (waterfallList.length === 0 && allRecommendListLoaded) {
    return null;
  }

  return (
    <div
      className={`bg-white  ${
        isWap ? 'mx-auto pt-[24px] px-[8px] rounded-t-[16px]' : 'mx-auto pt-[40px] px-[48px]'
      } pb-[60px]`}
    >
      {/* 标题 */}
      <h5
        ref={titleRef}
        className={`font-bb text-center ${
          isWap ? 'text-[20px] leading-[24px] mb-[16px]' : 'text-[32px] leading-[40px] mb-[32px]'
        }`}
      >
        {t('youMayAlsoLike')}
      </h5>

      {/* 商品网格 */}
      <div
        className={`grid ${
          isWap
            ? 'grid-cols-2 gap-x-[8px] gap-y-[40px]'
            : 'gap-x-[16px] gap-y-[24px] 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1'
        } ${isWap ? 'relative' : 'pb-[64px]'}`}
      >
        {waterfallList.map((item, index) => {
          const productForCard = {
            ...item,
            name: item.name || item.short_name || item.shop_sku || '', // ProductCard需要name属性
          };

          return (
            <div key={`${item.id}-${index}`} className="bg-none">
              <ProductCard
                product={productForCard}
                index={index}
                showTryOn={false}
                handelClick={() => handleItemClick(item, index)}
                onInView={() => handleProductInView(item, index)}
                className={isWap ? 'bg-none' : 'h-full bg-none'}
                imageClassName={isWap ? 'h-[176px] rounded-[12px]' : undefined}
                showCorrelation
              />
            </div>
          );
        })}
      </div>

      {/* 无限滚动加载 */}
      {!allRecommendListLoaded && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadRecommendData}
          page={0}
          total={1}
          limit={1}
          threshold={1}
          className={isWap ? 'pt-[40px]' : 'h-[64px] flex items-center justify-center'}
        />
      )}

      {/* 结束状态 */}
      {waterfallList.length > 0 && allRecommendListLoaded && (
        <div className="flex items-center justify-center py-[40px]">
          <div className="w-[24px] h-[1px] bg-auxiliary-dark-gray mr-[6px]" />
          <span className="text-[12px] text-auxiliary-dark-gray">{t('noMore')}</span>
          <div className="w-[24px] h-[1px] bg-auxiliary-dark-gray ml-[6px]" />
        </div>
      )}
    </div>
  );
}
